import { useQuery, type QueryClient } from '@tanstack/react-query';
import {
  folderDescendants,
  listFolder,
  locateLibraryItems,
  LOCATE_MAX_IDS,
  type FolderListing,
  type LibraryFile,
  type LibraryFolder,
  type LocatedDocumentEntry,
  type LocatedFolderEntry,
} from '@/api/library';
import { getUsersByIds } from '@/api/me';
import type { Descendants } from '@/features/chat/selection/selectionSet';

/**
 * Library reads shared by Guidelines & Context and the source dialog, cached
 * in react-query so both see the same folders and a change in one refreshes
 * the other.
 */

export const libraryKeys = {
  all: ['library'] as const,
  listing: (folderId: string | null) =>
    ['library', 'listing', folderId ?? 'root'] as const,
  descendants: (folderId: string) =>
    ['library', 'descendants', folderId] as const,
  search: (q: string, scope: string, folderId: string | null = null) =>
    ['library', 'search', q, scope, folderId ?? 'all'] as const,
};

/** Listings stay fresh this long; changes made here invalidate them at once. */
export const LISTING_STALE_MS = 30_000;

const toIds = (values: unknown[] | undefined): string[] =>
  (values ?? [])
    .map(value =>
      value && typeof value === 'object'
        ? String((value as { id?: unknown }).id ?? '')
        : String(value ?? ''),
    )
    .filter(id => /^\d+$/.test(id));

/**
 * Start loading a folder's listing before it is opened (pointer or focus on
 * its row), so opening it shows the cached rows at once.
 */
export const prefetchListing = (queryClient: QueryClient, folderId: string) =>
  void fetchListing(queryClient, folderId).catch(() => undefined);

export const fetchListing = (
  queryClient: QueryClient,
  folderId: string | null,
): Promise<FolderListing> =>
  queryClient.fetchQuery({
    queryKey: libraryKeys.listing(folderId),
    queryFn: async () => {
      const response = await listFolder(folderId);
      if (!response.success) {
        throw Object.assign(new Error(response.error || 'listing failed'), {
          failure: response,
        });
      }
      return response.data;
    },
    staleTime: LISTING_STALE_MS,
  });

export const useFolderListing = (folderId: string | null, enabled = true) =>
  useQuery({
    queryKey: libraryKeys.listing(folderId),
    queryFn: async () => {
      const response = await listFolder(folderId);
      if (!response.success) {
        throw Object.assign(new Error(response.error || 'listing failed'), {
          failure: response,
        });
      }
      return response.data;
    },
    staleTime: LISTING_STALE_MS,
    enabled,
  });

/** Resolved descendants from the cache, without a request. */
export const cachedDescendants = (
  queryClient: QueryClient,
  folderId: string,
): Descendants | undefined =>
  queryClient.getQueryData<Descendants>(libraryKeys.descendants(folderId));

const BATCH = 100;

const configured = new WeakSet<QueryClient>();

/**
 * Descendants are written by hand, so nothing observes them: keep them until
 * a change drops them (invalidateLibrary) instead of the default 5-minute GC.
 */
const keepDescendants = (queryClient: QueryClient) => {
  if (configured.has(queryClient)) return;
  configured.add(queryClient);
  queryClient.setQueryDefaults(['library', 'descendants'], {
    gcTime: Infinity,
  });
};

/**
 * Resolve the descendants of several folders, 100 per request, skipping the
 * ones already cached. Resolves to every requested folder that is known.
 */
export const ensureDescendants = async (
  queryClient: QueryClient,
  folderIds: readonly string[],
): Promise<Map<string, Descendants>> => {
  keepDescendants(queryClient);
  const unique = [...new Set(folderIds.filter(id => /^\d+$/.test(id)))];
  const missing = unique.filter(id => !cachedDescendants(queryClient, id));
  for (let start = 0; start < missing.length; start += BATCH) {
    const batch = missing.slice(start, start + BATCH);
    const response = await folderDescendants(batch);
    if (!response.success) {
      throw Object.assign(new Error(response.error || 'descendants failed'), {
        failure: response,
      });
    }
    batch.forEach(id => {
      const value = response.data[id];
      queryClient.setQueryData<Descendants>(libraryKeys.descendants(id), {
        folderIds: toIds(value?.folder_ids),
        fileIds: toIds(value?.file_ids),
      });
    });
  }
  const resolved = new Map<string, Descendants>();
  unique.forEach(id => {
    const value = cachedDescendants(queryClient, id);
    if (value) resolved.set(id, value);
  });
  return resolved;
};

export interface FolderLink {
  id: string;
  name: string;
  standard: boolean;
  readOnly: boolean;
}

export const folderLink = (folder: LibraryFolder): FolderLink => ({
  id: String(folder.id),
  name: folder.name,
  standard: Boolean(folder.standard ?? folder.default),
  readOnly: Boolean(folder.read_only),
});

export interface Located {
  /** The folders above the item, top-down. */
  chain: FolderLink[];
  /** id, name, parent, standard and read-only (no dates or creator). */
  folder?: LibraryFolder;
  /**
   * id, name, type, status, standard and read-only. No presigned `path`:
   * the listing of the last folder in `chain` has it.
   */
  file?: LibraryFile;
}

type LocateRef = { kind: 'folder' | 'file'; id: string };

/** One located item as cached: null when it is gone for this user. */
type LocatedEntry =
  | { kind: 'folder'; entry: LocatedFolderEntry }
  | { kind: 'file'; entry: LocatedDocumentEntry }
  | null;

const locatedKey = (ref: LocateRef) =>
  ['library', 'located', ref.kind, ref.id] as const;

/** A cached lookup that is still fresh (no change in the library since). */
const freshLocated = (
  queryClient: QueryClient,
  ref: LocateRef,
): LocatedEntry | undefined => {
  const state = queryClient.getQueryState<LocatedEntry>(locatedKey(ref));
  if (!state || state.data === undefined || state.isInvalidated) return;
  if (Date.now() - state.dataUpdatedAt > LISTING_STALE_MS) return;
  return state.data;
};

/** Ask for the refs, LOCATE_MAX_IDS a request, and cache every answer. */
const fetchLocated = async (
  queryClient: QueryClient,
  refs: readonly LocateRef[],
) => {
  for (let start = 0; start < refs.length; start += LOCATE_MAX_IDS) {
    const chunk = refs.slice(start, start + LOCATE_MAX_IDS);
    const response = await locateLibraryItems({
      folderIds: chunk.filter(ref => ref.kind === 'folder').map(ref => ref.id),
      documentIds: chunk.filter(ref => ref.kind === 'file').map(ref => ref.id),
    });
    if (!response.success) {
      throw Object.assign(new Error(response.error || 'locate failed'), {
        failure: response,
      });
    }
    const found = new Map<string, LocatedEntry>();
    response.data.folders.forEach(entry =>
      found.set(`folder:${entry.id}`, { kind: 'folder', entry }),
    );
    response.data.documents.forEach(entry =>
      found.set(`file:${entry.id}`, { kind: 'file', entry }),
    );
    // Listed as missing, or not answered at all: gone for this user.
    chunk.forEach(ref =>
      queryClient.setQueryData<LocatedEntry>(
        locatedKey(ref),
        found.get(`${ref.kind}:${ref.id}`) ?? null,
      ),
    );
  }
};

/**
 * Lookups asked for in the same tick share one request: the callers that
 * name every selected folder at once (one locateItem each) cost one call.
 */
const pendingLocates = new WeakMap<
  QueryClient,
  { refs: Map<string, LocateRef>; done: Promise<void> }
>();

const requestLocated = (
  queryClient: QueryClient,
  refs: readonly LocateRef[],
): Promise<void> => {
  let batch = pendingLocates.get(queryClient);
  if (!batch) {
    const created = {
      refs: new Map<string, LocateRef>(),
      done: Promise.resolve(),
    };
    created.done = Promise.resolve().then(() => {
      pendingLocates.delete(queryClient);
      return fetchLocated(queryClient, [...created.refs.values()]);
    });
    pendingLocates.set(queryClient, created);
    batch = created;
  }
  const { refs: queued } = batch;
  refs.forEach(ref => queued.set(`${ref.kind}:${ref.id}`, ref));
  return batch.done;
};

const locatedOf = async (
  queryClient: QueryClient,
  refs: readonly LocateRef[],
): Promise<Map<string, LocatedEntry>> => {
  const missing = refs.filter(
    ref => freshLocated(queryClient, ref) === undefined,
  );
  if (missing.length > 0) await requestLocated(queryClient, missing);
  return new Map(
    refs.map(ref => [
      `${ref.kind}:${ref.id}`,
      queryClient.getQueryData<LocatedEntry>(locatedKey(ref)) ?? null,
    ]),
  );
};

/**
 * Find folders and documents by id through GET /llm/library/locate, with
 * the folders above them (their standard and read-only flags come from a
 * second lookup of those folders, usually cached). Lookups are cached until
 * the library changes and batched with others of the same tick. The map
 * holds null for an item that is gone for this user (deleted, inside a
 * deleted folder, or not visible); a failed request rejects, and is never
 * reported as gone.
 */
export const locateItems = async (
  queryClient: QueryClient,
  refs: readonly LocateRef[],
): Promise<Map<string, Located | null>> => {
  const entries = await locatedOf(queryClient, refs);
  const above = new Map<string, LocateRef>();
  entries.forEach(item =>
    item?.entry.path.forEach(link => {
      const id = String(link.id);
      above.set(id, { kind: 'folder', id });
    }),
  );
  const parents = await locatedOf(queryClient, [...above.values()]);
  const chainOf = (path: LocatedFolderEntry['path']): FolderLink[] =>
    path.map(link => {
      const parent = parents.get(`folder:${link.id}`);
      const flags = parent?.kind === 'folder' ? parent.entry : null;
      return {
        id: String(link.id),
        name: link.name,
        standard: Boolean(flags?.standard),
        readOnly: Boolean(flags?.read_only),
      };
    });
  const located = new Map<string, Located | null>();
  entries.forEach((item, key) => {
    if (!item) {
      located.set(key, null);
    } else if (item.kind === 'folder') {
      const { entry } = item;
      located.set(key, {
        chain: chainOf(entry.path),
        folder: {
          id: entry.id,
          name: entry.name,
          parent_id: entry.parent_id,
          standard: entry.standard,
          read_only: entry.read_only,
        },
      });
    } else {
      const { entry } = item;
      located.set(key, {
        chain: chainOf(entry.path),
        file: {
          id: entry.id,
          name: entry.name,
          type: entry.file_type ?? undefined,
          status: entry.status ?? undefined,
          default: entry.standard,
          read_only: entry.read_only,
        },
      });
    }
  });
  return located;
};

/**
 * Find one folder or document by id (see locateItems). Null when it is gone
 * for this user; rejects when the lookup failed.
 */
export const locateItem = async (
  queryClient: QueryClient,
  ref: LocateRef,
): Promise<Located | null> =>
  (await locateItems(queryClient, [ref])).get(`${ref.kind}:${ref.id}`) ?? null;

/** Forget descendants (they change with uploads, moves and deletions). */
export const dropDescendants = (queryClient: QueryClient) =>
  queryClient.removeQueries({ queryKey: ['library', 'descendants'] });

/**
 * After any change: listings, descendants and searches are all stale.
 * Descendants are written by hand, not fetched by a query function, so they
 * are dropped rather than marked stale.
 */
export const invalidateLibrary = (queryClient: QueryClient) => {
  dropDescendants(queryClient);
  return queryClient.invalidateQueries({ queryKey: libraryKeys.all });
};

const memberNames = new Map<string, string>();

/** Names of the users who created or updated the rows, by id. */
export const useMemberNames = (
  userIds: readonly (string | null | undefined)[],
) => {
  const missing = [
    ...new Set(userIds.filter((id): id is string => !!id && /^\d+$/.test(id))),
  ]
    .filter(id => !memberNames.has(id))
    .sort();
  const { data } = useQuery({
    queryKey: ['library', 'members', missing.join(',')],
    queryFn: async () => {
      const members = await getUsersByIds(missing);
      members.forEach(member => {
        if (member?.id !== undefined) {
          memberNames.set(
            String(member.id),
            String(member.name || member.email || member.id),
          );
        }
      });
      return members.length;
    },
    enabled: missing.length > 0,
    staleTime: Infinity,
  });
  void data;
  return (userId?: string | null) =>
    userId ? (memberNames.get(userId) ?? '') : '';
};
